import { useState, useCallback, memo, useEffect, useRef } from 'react';
import { ClientProps, ErrorProps, OfferItem } from '../../types/types';
import { IconsCertificate } from '../../assets/icons/certificatesIcons';
import Modal from '../Modal';
import Loading from '../../components/Loading';
import PageError from '../PageError';
import { useRenewal } from '../../context/RenewalContext';
import { postRequest } from '../../services/api';
import {
  TitleOffers,
  Container,
  ContentOffersList,
  OfferItemList,
  OffersUL,
  HeadTabOffers,
  ContentImage,
  TitleImage,
  Images,
  ContentInfos,
  NameCertificate,
  MidiaType,
  CertificateDuration,
  ContentPrice,
  Price,
  ButtonBuy
} from './styles';

interface OffersListProps {
  offersByCategory: Record<string, OfferItem[]>;
  clientInfos: ClientProps | null;
}

const IconRenderer = ({ name }: { name: keyof typeof IconsCertificate }) => {
  const Icon = IconsCertificate[name];
  return Icon ? <>{Icon}</> : null;
};

const OffersList = memo(({ offersByCategory, clientInfos }: OffersListProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<ErrorProps | null>(null);
  const [offerSelected, setOffer] = useState<OfferItem | undefined>(undefined);
  const categories = Object.keys(offersByCategory);
  const [selectedCategory, setSelectedCategory] = useState<string>(categories[0]);
  const [isPortability, setPortability] = useState<boolean>(false);
  const prevCategories = useRef<string[]>(categories);
  const { setData } = useRenewal();
  useEffect(() => {
    if (categories.length > 0 && categories[0] !== prevCategories.current[0]) {
      setSelectedCategory(categories[0]);
    }
    prevCategories.current = categories;
  }, [categories]);

  const handleCategoryChange = useCallback((category: string) => {
    setSelectedCategory(category);
  }, []);

  const offers = offersByCategory[selectedCategory];

  const handlePortability = (offer: OfferItem) => {
    if (!offer.urlRedirect &&
      offer.saleType === 'PORTABILITY' &&
      (!clientInfos?.cnpj || clientInfos.cnpj === '') &&
      offer.c_crts_certificateType !== 'ECPF') {
      setOffer(offer)
      setPortability(true);
    } else if (offer.urlRedirect) {
      window.location.href = offer.urlRedirect;
    } else {
      setLoading(true);
      setData((prevData) => {
        const updatedData = {
          ...prevData,
          selectedOffer: {
            sku: offer.sku,
            saleType: offer.saleType
          },
        };

        postRequest('/api/v1/select-offer', updatedData)
          .then((data) => {
            window.location.href = data.cartUrl
            setLoading(false);
          })
          .catch((error) => {
            console.error('Erro ao enviar dados para a API:', error);
            setError(error)
            setLoading(false);
          });

        return updatedData;
      });

      setPortability(false);
    }
  };

  return (
    <>
      {error ? <PageError error={error} /> : (
        <Container>
          {loading && <Loading loading={loading} />}
          {error && <PageError error={error} />}
          <HeadTabOffers>
            {categories.map((category) => (
              <TitleOffers
                key={category}
                onClick={() => handleCategoryChange(category)}
                $isActive={selectedCategory === category}
              >
                {category === 'ECPF' ? 'Pessoa Física' :
                  category === 'ECNPJ' ? 'Pessoa Jurídica' :
                    category === 'CTE' ? 'CT-e' :
                      category === 'NFE' ? 'NF-e' :
                        category}
              </TitleOffers>
            ))}
          </HeadTabOffers>
          <ContentOffersList>
            <OffersUL>
              {offers?.length ? (
                offers.map((offer, index) => (
                  <OfferItemList key={`${offer.sku}-${index}`}>
                    <ContentImage>
                      <TitleImage>Renove o seu Certificado Digital</TitleImage>
                      <Images>
                        <IconRenderer
                          name={
                            offer.c_crts_type_variation === 'somente-certificado' ? 'onlyCertificate' :
                              offer.c_crts_type_variation === 'na-nuvem' ? 'cloud' :
                                offer.c_crts_type_variation === 'no-computador' ? 'computer' : 'null'
                          }
                        />
                        <IconRenderer
                          name={
                            offer.c_crts_certificateType === 'ECPF' ? 'ecpf' :
                              offer.c_crts_certificateType === 'ECNPJ' ? 'ecnpj' :
                                offer.c_crts_certificateType === 'CTE' ? 'cte' :
                                  offer.c_crts_certificateType === 'PE' ? 'pe' :
                                    offer.c_crts_certificateType === 'NFE' ? 'nfe' : 'null'
                          }
                        />
                      </Images>
                    </ContentImage>
                    <ContentInfos>
                      <NameCertificate>
                        {
                          offer.c_crts_certificateType === 'ECPF' ? 'Certificado digital e-CPF' :
                            offer.c_crts_certificateType === 'ECNPJ' ? 'Certificado digital e-CNPJ' :
                              offer.c_crts_certificateType === 'CTE' ? 'Certificado digital CT-e' :
                                offer.c_crts_certificateType === 'PE' ? 'Certificado digital PME' :
                                  offer.c_crts_certificateType === 'NFE' ? 'Certificado digital NF-e' : 'null'
                        }{' '}{offer.c_crts_ac !== 'CERTISIGN' && offer.c_crts_ac !== 'CONCORRENTE' ? offer.c_crts_ac : ''}
                      </NameCertificate>
                      <MidiaType>Mídia:
                        {offer.c_crts_type_variation === 'somente-certificado' ? ' Somente Certificado' :
                          offer.c_crts_type_variation === 'no-computador' ? ' No computador' :
                            offer.c_crts_type_variation === 'na-nuvem' ? ' Na nuvem' : offer.c_crts_type_variation}</MidiaType>
                      <CertificateDuration>Validade: <span>{offer.c_crts_certificate_duration.replace(/\D/g, '')} MESES</span></CertificateDuration>
                    </ContentInfos>
                    <ContentPrice>
                      <Price>Total: <strong>{offer.price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', })}</strong></Price>
                    </ContentPrice>
                    <ButtonBuy onClick={() => handlePortability(offer)}>
                      {offer.saleType === 'RENEWAL' ? 'Renove agora' :
                        offer.saleType === 'PORTABILITY' ? 'Fazer a portabilidade agora' :
                          offer.saleType === 'NEW_PURCHASE' ? 'Realizar nova compra agora' : ''
                      }
                    </ButtonBuy>
                  </OfferItemList>
                ))
              ) : (
                <p>Nenhuma oferta disponível.</p>
              )}
            </OffersUL>
          </ContentOffersList>
          {isPortability && <Modal setPortability={setPortability} offer={offerSelected} setLoading={setLoading} />}
        </Container>
      )}
    </>
  );
});

export default OffersList;
